import * as React from 'react'

import { InfoIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Localization from '../../localization/LocalizationManager'
import LocationBase from '../../models/LocationObjects/LocationBase'
import Workflow from '../../models/Workflow'
import CompaniesStore from '../../stores/domain/Companies.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import ProjectMembersStore from '../../stores/domain/ProjectMembers.store'
import WorkflowCategoriesStore from '../../stores/domain/WorkflowCategories.store'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import {
  getWorkflowGroupingVisualData,
  GroupVisualData,
} from '../../utils/workflowGroupingVisualData'
import { ICardListGroup } from '../CardList/types'
import { GroupHeader, GroupHeaderTrigger } from '../GroupHeader'
import { Checkbox } from '../shadcn-ui/checkbox'
import SitemapAttributeIconComponent from '../SitemapAttributeTag/SitemapAttributeIcon'
import SitemapAttributeTag from '../SitemapAttributeTag/SitemapAttributeTag'
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper'
import { WorkflowGroupingKey } from './WorkflowList.types'

interface IProps {
  group: ICardListGroup<Workflow>
  groupingKey: WorkflowGroupingKey | null
  isReadOnly: boolean
  isMultiple?: boolean
  onToggle: () => void

  checked?: boolean
  indeterminate?: boolean
  onCheckboxClick?: () => void

  /** `menu`: the compact select-menu band used by pickers. */
  variant?: 'list' | 'menu'
  /** Menu only: separates the band from the rows of the group above it. */
  hasTopBorder?: boolean

  workflowTypesStore?: WorkflowTypesStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  workflowCategoriesStore?: WorkflowCategoriesStore
}

const WorkflowGroupHeader: React.FC<IProps> = observer(
  ({
    group,
    groupingKey,
    isReadOnly,
    isMultiple = true,
    onToggle,
    checked = false,
    indeterminate = false,
    onCheckboxClick,
    variant = 'list',
    hasTopBorder,
    workflowTypesStore,
    companiesStore,
    projectMembersStore,
    locationAttributesStore,
    workflowCategoriesStore,
  }) => {
    const visualData = groupingKey
      ? getWorkflowGroupingVisualData({
          groupingKey,
          categoryId: group.key,
          permitTypesStore: workflowTypesStore,
          formCategoriesStore: workflowCategoriesStore,
          companiesStore,
          projectMembersStore,
          locationAttributesStore,
        })
      : null

    const { labelNode, labelIcon, suffixContent } = resolveVisual(visualData)

    const isSelectable = !isReadOnly && isMultiple

    if (variant === 'menu') {
      return (
        // Wrapped so CardList's last-row `[&>*]:border-b-0` lands here, keeping the band's bottom border.
        <div>
          <GroupHeader
            role="group"
            aria-label={group.label}
            className={classList({ 'tw:border-t': !!hasTopBorder })}
          >
            {isSelectable && (
              <Checkbox
                checked={checked}
                indeterminate={indeterminate}
                aria-label={Localization.translator.selectX(group.label)}
                onCheckedChange={() => onCheckboxClick?.()}
              />
            )}
            <GroupHeaderTrigger
              expanded={!group.isCollapsed}
              onClick={onToggle}
              ariaLabel={group.label}
              labelIcon={labelIcon}
              // Custom visuals (e.g. the status badge) already carry the group's name.
              label={labelNode ?? group.label}
              count={group.count}
            />
            {suffixContent}
          </GroupHeader>
        </div>
      )
    }

    return (
      // Logistics rows centre their checkbox in a 40px column (12px in); match it so the
      // band checkbox lines up with the row and counter checkboxes.
      <GroupHeader className={isSelectable ? 'tw:pl-3' : undefined}>
        {isSelectable && (
          <span
            className="tw:flex tw:shrink-0 tw:items-center"
            onClick={e => e.stopPropagation()}
          >
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              aria-label={Localization.translator.selectX(group.label)}
              onCheckedChange={() => onCheckboxClick?.()}
            />
          </span>
        )}
        <GroupHeaderTrigger
          expanded={!group.isCollapsed}
          onClick={onToggle}
          ariaLabel={group.label}
          labelIcon={labelIcon}
          // Custom visuals (e.g. the status badge) already carry the group's name,
          // so they replace the text label instead of prefixing it.
          label={labelNode ?? group.label}
          count={group.count}
        />
        {suffixContent}
      </GroupHeader>
    )
  },
)

function resolveVisual(visualData: GroupVisualData | null): {
  labelNode?: React.ReactNode
  labelIcon?: React.ReactNode
  suffixContent?: React.ReactNode
} {
  if (!visualData) return {}

  switch (visualData.kind) {
    case 'custom':
      return { labelNode: visualData.labelNode }
    case 'tag':
      return {
        labelIcon: (
          <SitemapAttributeIconComponent
            dataObject={
              {
                color: visualData.labelColor || visualData.color,
                iconName: visualData.iconName,
              } as unknown as LocationBase
            }
          />
        ),
      }
    case 'location':
      return {
        labelIcon: (
          <SitemapAttributeTag
            shouldShowAsTag={false}
            dataObject={visualData.dataObject}
          />
        ),
      }
    case 'icon': {
      const CustomIcon = visualData.icon
      return {
        labelIcon: <CustomIcon {...visualData.iconProps} />,
        suffixContent: visualData.infoTooltip ? (
          <TooltipWrapper
            content={visualData.infoTooltip}
            className="tw:flex tw:shrink-0 tw:items-center"
          >
            <InfoIcon className="tw:size-4 tw:text-muted-foreground" />
          </TooltipWrapper>
        ) : undefined,
      }
    }
  }
}

export default inject(
  'workflowTypesStore',
  'companiesStore',
  'projectMembersStore',
  'locationAttributesStore',
  'workflowCategoriesStore',
)(WorkflowGroupHeader)
